import { useState } from 'react';

interface PropertySchema {
  type?: string | string[];
  enum?: unknown[];
  format?: string;
  description?: string;
  minimum?: number;
  maximum?: number;
  maxLength?: number;
  pattern?: string;
}

interface ObjectSchema {
  properties?: Record<string, PropertySchema>;
  required?: string[];
}

export type Attributes = Record<string, unknown>;

function asObjectSchema(schema: unknown): ObjectSchema {
  return typeof schema === 'object' && schema !== null ? (schema as ObjectSchema) : {};
}

function primaryType(p: PropertySchema): string {
  if (p.enum) return 'enum';
  const t = Array.isArray(p.type) ? p.type.find((x) => x !== 'null') : p.type;
  return t ?? 'string';
}

/**
 * Renders inputs for the flat properties of a JSON Schema object (the shape
 * every rule set uses for class-specific attributes). Unknown constructs fall
 * back to a raw JSON editor so nothing the schema allows is unreachable.
 */
export function AttributesForm({
  schema,
  value,
  onChange,
  errors = {},
}: {
  schema: unknown;
  value: Attributes;
  onChange: (next: Attributes) => void;
  errors?: Record<string, string>;
}) {
  const obj = asObjectSchema(schema);
  const props = Object.entries(obj.properties ?? {});
  const required = new Set(obj.required ?? []);
  const [raw, setRaw] = useState(false);
  const [rawText, setRawText] = useState(() => JSON.stringify(value, null, 2));
  const [rawError, setRawError] = useState<string | null>(null);

  const set = (key: string, v: unknown) => {
    const next = { ...value };
    if (v === undefined || v === '') delete next[key];
    else next[key] = v;
    onChange(next);
  };

  if (raw || props.length === 0) {
    return (
      <fieldset>
        <legend>Class attributes {props.length > 0 && <button type="button" className="linklike" onClick={() => setRaw(false)}>(use form)</button>}</legend>
        <label>
          JSON
          <textarea
            rows={8}
            value={rawText}
            onChange={(e) => {
              setRawText(e.target.value);
              try {
                const parsed: unknown = JSON.parse(e.target.value);
                if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error('must be a JSON object');
                setRawError(null);
                onChange(parsed as Attributes);
              } catch (err) {
                setRawError((err as Error).message);
              }
            }}
          />
          {rawError && <span className="hint error">{rawError}</span>}
          {props.length === 0 && <span className="hint">This rule set defines no specific attributes for this record type.</span>}
        </label>
      </fieldset>
    );
  }

  return (
    <fieldset>
      <legend>
        Class attributes{' '}
        <button
          type="button"
          className="linklike"
          onClick={() => {
            setRawText(JSON.stringify(value, null, 2));
            setRaw(true);
          }}
        >
          (edit as JSON)
        </button>
      </legend>
      <div className="grid2">
        {props.map(([key, p]) => {
          const kind = primaryType(p);
          const label = `${key}${required.has(key) ? ' *' : ''}`;
          const err = errors[`attributes.${key}`];
          const current = value[key];
          let control: JSX.Element;
          if (kind === 'enum') {
            control = (
              <select value={current === undefined ? '' : String(current)} onChange={(e) => set(key, e.target.value === '' ? undefined : (p.enum ?? []).find((o) => String(o) === e.target.value))}>
                <option value="">—</option>
                {(p.enum ?? []).map((o) => (
                  <option key={String(o)} value={String(o)}>
                    {String(o)}
                  </option>
                ))}
              </select>
            );
          } else if (kind === 'boolean') {
            control = (
              <select value={current === undefined ? '' : String(current)} onChange={(e) => set(key, e.target.value === '' ? undefined : e.target.value === 'true')}>
                <option value="">—</option>
                <option value="true">yes</option>
                <option value="false">no</option>
              </select>
            );
          } else if (kind === 'number' || kind === 'integer') {
            control = (
              <input
                type="number"
                step={kind === 'integer' ? 1 : 'any'}
                min={p.minimum}
                max={p.maximum}
                value={current === undefined ? '' : String(current)}
                onChange={(e) => set(key, e.target.value === '' ? undefined : Number(e.target.value))}
              />
            );
          } else {
            control = (
              <input
                type={p.format === 'date' ? 'date' : 'text'}
                maxLength={p.maxLength}
                value={current === undefined ? '' : String(current)}
                onChange={(e) => set(key, e.target.value)}
              />
            );
          }
          return (
            <label key={key}>
              {label}
              {control}
              {(err || p.description) && <span className={`hint${err ? ' error' : ''}`}>{err ?? p.description}</span>}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export function AttributesList({ attributes }: { attributes: Attributes }) {
  const entries = Object.entries(attributes);
  if (entries.length === 0) return <span className="muted">none</span>;
  return (
    <dl className="dl">
      {entries.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}
